import React, { useMemo } from 'react';
import { Badge, Modal, Spinner } from 'react-bootstrap';
import {
  MdCancel,
  MdComputer,
  MdHome,
  MdInfoOutline,
  MdOutlineVolumeUp,
  MdPerson,
  MdTaskAlt,
  MdTimerOff,
} from 'react-icons/md';

function formatDateTime(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'medium',
    timeZone: 'America/Sao_Paulo',
  }).format(date);
}

const STATUS_CONFIG = {
  calling: {
    label: 'Chamando',
    bg: 'warning',
    textColor: 'text-dark',
    icon: MdOutlineVolumeUp,
  },
  arrived: {
    label: 'Na sala',
    bg: 'info',
    textColor: 'text-dark',
    icon: MdHome,
  },
  done: {
    label: 'Atendido',
    bg: 'success',
    textColor: 'text-white',
    icon: MdTaskAlt,
  },
  expired: {
    label: 'Expirado (TV)',
    bg: 'danger',
    textColor: 'text-white',
    icon: MdTimerOff,
  },
  canceled: {
    label: 'Cancelado',
    bg: 'danger',
    textColor: 'text-white',
    icon: MdCancel,
  },
};

const EVENT_LABELS = {
  create: 'Chamada Iniciada',
  recall: 'Rechamada no Painel',
  recall_create: 'Nova Chamada Solicitada',
  cancel_call: 'Cancelamento de Chamada',
  reopen_call: 'Reabertura da Chamada',
  status_change: 'Mudança de Status',
  expire: 'Tempo Expirado',
};

function formatSource(source) {
  const sources = {
    api: 'Painel do Médico',
    scheduler: 'Sistema (Automático)',
    reception: 'Recepção',
  };
  return sources[source] || source || 'Sistema';
}

function getActorDisplay(event, call, agenda) {
  if (event?.source === 'scheduler') {
    return 'Cronômetro Automático';
  }

  const doctorName = call?.CODINOME || agenda?.NOMEMEDICO || agenda?.MEDICO || agenda?.NOMEMED;

  if (doctorName) {
    return `${doctorName}`;
  }

  if (event?.actor?.nome) {
    const role = event?.actor?.role ? ` (${event.actor.role})` : '';
    return `${event.actor.nome}${role}`;
  }

  return 'Médico Responsável';
}

export default function HistoricoDeChamadas({
  show,
  loading,
  events = [],
  error,
  agenda,
  call,
  onHide,
}) {
  const medicoExibicao = call?.CODINOME || agenda?.NOMEMEDICO || agenda?.MEDICO || agenda?.NOMEMED;

  // Ordena os eventos para exibir os mais recentes no topo
  const sortedEvents = useMemo(() => {
    if (!Array.isArray(events)) return [];
    return [...events].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [events]);

  return (
    <Modal show={show} onHide={onHide} centered size="lg" backdrop="static">
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title className="fs-5 fw-bold text-primary d-flex align-items-center gap-2">
          <MdInfoOutline size={22} />
          Histórico do Atendimento
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="pt-2 px-4 pb-4">
        {/* Cabeçalho de informações do paciente e médico */}
        <div className="p-3 mb-3 bg-light rounded-3 border d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div>
            <span className="text-muted small d-block">Paciente</span>
            <strong className="fs-6 text-dark">{agenda?.NOME || '-'}</strong>
          </div>
          {medicoExibicao && (
            <div>
              <span className="text-muted small d-block">Médico</span>
              <span className="fw-semibold text-dark">{medicoExibicao}</span>
            </div>
          )}
          <div>
            <span className="text-muted small d-block">Procedimento</span>
            <span className="fw-semibold text-secondary">{agenda?.PROCEDIMENTO || '-'}</span>
          </div>
          <div>
            <span className="text-muted small d-block">Total de Eventos</span>
            <Badge bg="secondary" pill>
              {sortedEvents.length} registro(s)
            </Badge>
          </div>
        </div>

        {/* Corpo do Histórico */}
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" role="status" />
            <p className="text-muted small mt-2">Carregando histórico...</p>
          </div>
        ) : error ? (
          <div className="alert alert-danger mb-0" role="alert">
            {error}
          </div>
        ) : sortedEvents.length === 0 ? (
          <div className="alert alert-secondary text-center my-3" role="alert">
            Nenhum evento registrado até o momento.
          </div>
        ) : (
          <div className="timeline-container position-relative px-2 py-2">
            {sortedEvents.map((event, index) => {
              const toStatusConfig = STATUS_CONFIG[event.toStatus] || STATUS_CONFIG.calling;
              const IconComponent = toStatusConfig.icon;
              const eventTitle = EVENT_LABELS[event.event] || event.event || 'Ação registrada';
              const hasStatusChange = event.fromStatus && event.fromStatus !== event.toStatus;

              return (
                <div
                  key={event.id || `${event.createdAt}-${index}`}
                  className="timeline-item position-relative d-flex gap-3 pb-3"
                >
                  {index < sortedEvents.length - 1 && (
                    <div
                      style={{
                        position: 'absolute',
                        left: '19px',
                        top: '36px',
                        bottom: '0',
                        width: '2px',
                        backgroundColor: '#e9ecef',
                      }}
                    />
                  )}

                  <div
                    className={`rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 bg-${toStatusConfig.bg} ${toStatusConfig.textColor}`}
                    style={{
                      width: '40px',
                      height: '40px',
                      zIndex: 1,
                      boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
                    }}
                  >
                    <IconComponent size={20} />
                  </div>

                  <div className="flex-grow-1 bg-white border rounded-3 p-3 shadow-sm">
                    <div className="d-flex justify-content-between align-items-start flex-wrap gap-1 mb-2">
                      <div className="d-flex align-items-center gap-2">
                        <strong className="text-dark" style={{ fontSize: '0.95rem' }}>
                          {eventTitle}
                        </strong>
                        <Badge
                          bg={toStatusConfig.bg}
                          className={`${toStatusConfig.textColor} fw-normal px-2 py-1`}
                          style={{ fontSize: '0.75rem' }}
                        >
                          {toStatusConfig.label}
                        </Badge>
                      </div>
                      <small className="text-muted fw-medium">
                        {formatDateTime(event.createdAt)}
                      </small>
                    </div>

                    {hasStatusChange && (
                      <div className="text-muted small mb-2 d-flex align-items-center gap-1">
                        <span>Status anterior:</span>
                        <span className="fw-semibold text-dark">
                          {STATUS_CONFIG[event.fromStatus]?.label || event.fromStatus}
                        </span>
                        <span>&rarr;</span>
                        <span className="fw-semibold text-dark">{toStatusConfig.label}</span>
                      </div>
                    )}

                    <div
                      className="d-flex gap-3 text-muted border-top pt-2 mt-2"
                      style={{ fontSize: '0.82rem' }}
                    >
                      <span className="d-flex align-items-center gap-1">
                        <MdComputer size={15} />
                        {formatSource(event.source)}
                      </span>
                      <span className="d-flex align-items-center gap-1">
                        <MdPerson size={15} />
                        {getActorDisplay(event, call, agenda)}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
}
